"use client";

import Link from "next/link";
import { useState } from "react";
import { motion } from "framer-motion";
import { IndianRupee } from "lucide-react";
import { donationTiers, getDonateTierHref } from "@/content/home";
import { cn } from "@/lib/utils";

export function CharityDonateTiers({
  variant = "light",
}: {
  variant?: "light" | "dark";
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const isDark = variant === "dark";

  return (
    <div className="relative">
      {hovered !== null && (
        <div
          className={cn(
            "pointer-events-none absolute -inset-4 rounded-3xl blur-2xl",
            isDark ? "bg-[var(--charity-gold)]/15" : "bg-[var(--charity-gold)]/10"
          )}
          aria-hidden
        />
      )}
      <div className="relative grid grid-cols-1 gap-3 min-[480px]:grid-cols-3">
        {donationTiers.map((tier) => (
          <Link
            key={tier.amount}
            href={getDonateTierHref(tier.amount)}
            onMouseEnter={() => setHovered(tier.amount)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(tier.amount)}
            onBlur={() => setHovered(null)}
            className={cn(
              "charity-donate-tier group relative rounded-2xl border p-4 text-center",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--charity-gold)] focus-visible:ring-offset-2",
              isDark
                ? "border-white/20 bg-white/10 focus-visible:ring-offset-[var(--charity-green-dark)]"
                : "border-[var(--charity-stone)] bg-[var(--card)] focus-visible:ring-primary focus-visible:ring-offset-2",
              hovered === tier.amount &&
                (isDark
                  ? "border-[var(--charity-gold)] bg-[var(--charity-gold)]/20 shadow-[0_8px_24px_rgba(244,201,93,0.3)]"
                  : "border-[var(--charity-gold)] bg-[var(--charity-gold)]/15 shadow-[0_8px_24px_rgba(244,201,93,0.25)]")
            )}
          >
            <motion.div
              className={cn(
                "mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full",
                isDark
                  ? "bg-[var(--charity-gold)]/20 text-[var(--charity-gold)]"
                  : "bg-primary/10 text-primary"
              )}
              animate={
                hovered === tier.amount
                  ? { scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }
                  : { scale: 1, opacity: 0.85 }
              }
              transition={{
                duration: 1.2,
                repeat: hovered === tier.amount ? Infinity : 0,
              }}
              aria-hidden
            >
              <IndianRupee size={16} />
            </motion.div>
            <p
              className={cn(
                "text-lg font-bold md:text-xl",
                isDark ? "text-[var(--charity-gold)]" : "text-foreground"
              )}
            >
              ₹{tier.amount.toLocaleString("en-IN")}
            </p>
            <p
              className={cn(
                "mt-1 text-xs",
                isDark ? "text-white/75" : "text-muted"
              )}
            >
              {tier.impact}
            </p>
            <p
              className={cn(
                "mt-2 text-xs font-semibold opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-visible:opacity-100",
                isDark ? "text-[var(--charity-gold)]" : "text-primary"
              )}
            >
              Donate now →
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
